// ============================================================================
// BEHAVIOR LOADER - Sistema di inizializzazione automatica componenti
// ============================================================================
// Gestisce l'inizializzazione automatica di componenti in base a data-behavior
// Usa MutationObserver per elementi aggiunti dinamicamente
// ============================================================================

import errorManager from '@/rollbar.js';
import { behaviors } from '@/behaviors/behaviors.js';

// WeakMap per tracciare elementi già inizializzati (evita doppie inizializzazioni)
const initializedElements = new WeakMap();

/**
 * Estrae i parametri di inizializzazione da un elemento
 * @param {HTMLElement} el - L'elemento con data-behavior
 * @returns {Object} Parametri per l'init
 */
function getInitParams(el) {
  let params = {};

  // data-behavior-args: JSON con tutti gli argomenti
  if (el.dataset.behaviorArgs) {
    try {
      params = { ...params, ...JSON.parse(el.dataset.behaviorArgs) };
    } catch (e) {
      errorManager(new Error(`Errore parsing data-behavior-args: ${e.message}`));
      console.error('[BehaviorLoader] Invalid JSON in data-behavior-args:', el.dataset.behaviorArgs);
    }
  }

  return params;
}

/**
 * Inizializza un elemento singolo
 * @param {HTMLElement} el - Elemento con data-behavior
 */
async function initElement(el) {
  const behaviorAlias = el.dataset.behavior;
  if (!behaviorAlias) return;

  // Controlla se l'elemento è già inizializzato
  if (initializedElements.has(el)) return;

  try {
    // Verifica che il behavior esista in behaviors.js
    const globObj = behaviors[behaviorAlias];
    if (!globObj || typeof globObj !== 'object') {
      throw new Error(`Behavior "${behaviorAlias}" non trovato in behaviors.js`);
    }
    // Prendi la funzione di import dinamico (c'è sempre una sola chiave)
    const importFn = Object.values(globObj)[0];
    if (typeof importFn !== 'function') {
      throw new Error(`Behavior "${behaviorAlias}" non ha una funzione di import valida`);
    }
    // Carica il modulo tramite la funzione di import
    const module = await importFn();

    // Estrae i parametri di inizializzazione
    const params = getInitParams(el);

    // Cerca la funzione behavior() nel modulo
    const behaviorFn = module.behavior || module.default?.behavior;

    if (typeof behaviorFn === 'function') {
      // Chiama behavior() passando i parametri
      behaviorFn(params);
    } else {
      console.warn(`[BehaviorLoader] Modulo "${behaviorAlias}" non ha una funzione behavior()`);
    }

    // Segna l'elemento come inizializzato
    initializedElements.set(el, { behaviorAlias, module, params });
  } catch (error) {
    errorManager(error);
    console.error(`[BehaviorLoader] Errore inizializzazione behavior "${behaviorAlias}":`, error);
  }
}

/**
 * Inizializza tutti gli elementi già presenti nel DOM
 */
async function initAllExisting() {
  const elements = document.querySelectorAll('[data-behavior]');

  // Inizializza in sequenza per evitare race conditions
  for (const el of elements) {
    await initElement(el);
  }
}

// MutationObserver per nuovi elementi
let observer = null;

/**
 * Avvia l'osservazione del DOM per nuovi elementi
 * @param {HTMLElement} container - Container da osservare (default: document.body)
 */
function startObserving(container = document.body) {
  if (observer) {
    observer.disconnect();
  }

  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Solo nodi aggiunti
      mutation.addedNodes.forEach((node) => {
        // Solo elementi HTML
        if (node.nodeType !== 1) return;

        // Se il nodo stesso ha data-behavior
        if (node.dataset?.behavior) {
          initElement(node);
        }

        // Cerca sotto-elementi con data-behavior
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-behavior]').forEach(initElement);
        }
      });
    }
  });

  // Osserva childList e subtree per catturare anche elementi annidati
  observer.observe(container, {
    childList: true,
    subtree: true,
  });
}

/**
 * Inizializzazione principale del behavior loader
 */
async function init() {
  try {
    // Inizializza elementi già presenti
    await initAllExisting();

    // Avvia osservazione per nuovi elementi
    startObserving();

    console.log('[BehaviorLoader] Inizializzato con successo');
  } catch (error) {
    errorManager(error);
    console.error('[BehaviorLoader] Errore inizializzazione:', error);
  }
}

document.addEventListener('DOMContentLoaded', init);
